import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Database, UserX, Sparkles, Brain, Download, ArrowRight, Moon, Sun, KeyRound } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PILLS_DATA = [
  { id: 1, label: 'Privacy', icon: Shield, color: 'text-indigo-400', initialPos: { top: '18%', left: '12%' }, rotate: -5 },
  { id: 2, label: 'Local-first', icon: Database, color: 'text-blue-400', initialPos: { top: '42%', left: '25%' }, rotate: 3 },
  { id: 3, label: 'No Accounts', icon: UserX, color: 'text-purple-400', initialPos: { top: '12%', left: '58%' }, rotate: -2 },
  { id: 4, label: 'Refine', icon: Sparkles, color: 'text-emerald-400', initialPos: { top: '48%', left: '72%' }, rotate: 7 },
  { id: 5, label: 'Challenge', icon: Brain, color: 'text-amber-400', initialPos: { top: '72%', left: '18%' }, rotate: -4 },
  { id: 6, label: 'Export', icon: Download, color: 'text-rose-400', initialPos: { top: '68%', left: '55%' }, rotate: 2 },
  { id: 7, label: 'BYOK', icon: KeyRound, color: 'text-sky-400', initialPos: { top: '30%', left: '75%' }, rotate: -6 },
];

const PhysicsPill = ({ pill, containerRef }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const pillRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (containerRef.current && pillRef.current && !isInitialized) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const topPercent = parseFloat(pill.initialPos.top) / 100;
      const leftPercent = parseFloat(pill.initialPos.left) / 100;
      setPosition({
        x: containerRect.width * leftPercent,
        y: containerRect.height * topPercent
      });
      setIsInitialized(true);
    }
  }, [containerRef, pill.initialPos, isInitialized]);

  const handleMouseDown = (e) => {
    if (!pillRef.current) return;
    e.preventDefault();
    const rect = pillRef.current.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    if (!pillRef.current) return;
    const touch = e.touches[0];
    const rect = pillRef.current.getBoundingClientRect();
    offsetRef.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current || !pillRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      let x = e.clientX - containerRect.left - offsetRef.current.x;
      let y = e.clientY - containerRect.top - offsetRef.current.y;
      x = Math.max(0, Math.min(x, containerRect.width - pillRef.current.offsetWidth));
      y = Math.max(0, Math.min(y, containerRect.height - pillRef.current.offsetHeight));
      setPosition({ x, y });
    };

    const handleTouchMove = (e) => {
      if (!isDragging || !containerRef.current || !pillRef.current) return;
      const touch = e.touches[0];
      const containerRect = containerRef.current.getBoundingClientRect();
      let x = touch.clientX - containerRect.left - offsetRef.current.x;
      let y = touch.clientY - containerRect.top - offsetRef.current.y;
      x = Math.max(0, Math.min(x, containerRect.width - pillRef.current.offsetWidth));
      y = Math.max(0, Math.min(y, containerRect.height - pillRef.current.offsetHeight));
      setPosition({ x, y });
    };

    const handleEnd = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, containerRef]);

  const Icon = pill.icon;

  return (
    <div
      ref={pillRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`
        absolute px-5 py-2.5 md:px-7 md:py-3 rounded-full select-none
        bg-card/70 dark:bg-card/50 backdrop-blur-xl border border-border/50
        shadow-[0_4px_15px_rgba(0,0,0,0.03)] dark:shadow-[0_4px_15px_rgba(0,0,0,0.2)]
        transition-shadow duration-200
        ${isDragging 
          ? 'cursor-grabbing scale-105 shadow-[0_10px_25px_rgba(124,141,255,0.15)] border-indigo-300/30 z-50' 
          : 'cursor-grab z-10 hover:bg-card hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] dark:hover:shadow-[0_8px_20px_rgba(0,0,0,0.3)]'
        }
      `}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: isDragging ? 'rotate(0deg) scale(1.05)' : `rotate(${pill.rotate}deg)`,
      }}
    >
      <span className="flex items-center gap-2 md:gap-2.5 text-foreground/80 text-sm md:text-[0.95rem] tracking-wide font-medium">
        <Icon className={`w-4 h-4 ${pill.color}`} />
        {pill.label}
      </span>
    </div>
  );
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="relative min-h-screen flex flex-col items-center overflow-hidden app-gradient font-sans antialiased">
      {/* Noise texture */}
      <div 
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.02] dark:opacity-[0.03]"
        style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/noise.png')" }}
      />

      {/* Gradient blobs */}
      <div className="fixed top-[15%] left-[10%] w-[200px] md:w-[300px] h-[200px] md:h-[300px] rounded-full bg-blue-100/30 dark:bg-blue-900/20 blur-[80px] md:blur-[100px] -z-10" />
      <div className="fixed bottom-[10%] right-[5%] w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full bg-purple-100/30 dark:bg-purple-900/20 blur-[100px] md:blur-[120px] -z-10" />

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed top-6 right-6 z-50 p-3 rounded-full bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm hover:shadow-md transition-all duration-200"
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? (
          <Sun className="w-5 h-5 text-amber-400" />
        ) : (
          <Moon className="w-5 h-5 text-slate-600" />
        )}
      </button>

      {/* Header */}
      <header className="w-full max-w-7xl px-6 md:px-8 pt-8 md:pt-10 flex flex-col items-center z-50">
        <div className="flex items-center gap-3 mb-4 group cursor-pointer">
          <div className="w-10 h-10 rounded-xl bg-card shadow-sm flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform duration-200">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 48 48">
              <path d="M42.1739 20.1739L27.8261 5.82609C29.1366 7.13663 28.3989 10.1876 26.2002 13.7654C24.8538 15.9564 22.9595 18.3449 20.6522 20.6522C18.3449 22.9595 15.9564 24.8538 13.7654 26.2002C10.1876 28.3989 7.13663 29.1366 5.82609 27.8261L20.1739 42.1739C21.4845 43.4845 24.5355 42.7467 28.1133 40.548C30.3042 39.2016 32.6927 37.3073 35 35C37.3073 32.6927 39.2016 30.3042 40.548 28.1133C42.7467 24.5355 43.4845 21.4845 42.1739 20.1739Z" fill="currentColor"/>
            </svg>
          </div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">IdeaVault</h2>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extralight tracking-tight text-foreground mt-2 text-center px-4">
          Your private room for thoughts
        </h1>
      </header>

      {/* Interactive area */}
      <main className="w-full flex-grow relative overflow-hidden">
        <div 
          ref={containerRef}
          className="relative max-w-6xl mx-auto flex items-center justify-center"
          style={{ height: 'calc(100vh - 280px)', minHeight: '300px', perspective: '1000px' }}
        >
          {PILLS_DATA.map((pill) => (
            <PhysicsPill key={pill.id} pill={pill} containerRef={containerRef} />
          ))}
        </div>
      </main>

      {/* CTA */}
      <div className="fixed bottom-8 md:bottom-[60px] left-1/2 -translate-x-1/2 z-50 w-full flex flex-col items-center px-4">
        <button
          onClick={() => navigate('/app')}
          className="px-8 md:px-12 py-4 md:py-5 rounded-full bg-primary text-primary-foreground font-medium text-base md:text-lg flex items-center gap-3 shadow-2xl hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 min-h-[56px]"
          data-testid="start-thinking-btn"
        >
          <span>Start Thinking</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default LandingPage;
